import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { IconsEnum } from '@uigy/generated/icons.enum';
import { IconComponent } from '@uigy/shared/components/icons/icon/icon.component';
import { StateIndicatorType } from '@uigy/shared/components/icons/interfaces';

@Component({
  selector: 'app-ui-state-indicator',
  changeDetection: ChangeDetectionStrategy.OnPush,

  imports: [
    IconComponent
  ],
  templateUrl: './ui-state-indicator.component.html',
  styleUrls: ['./ui-state-indicator.component.scss']
})
/**
 * Component to display a state/status with an icon and optional text.
 * Default use is to apply StateIndicatorType enum to represent different states.
 * If different states are needed, generic type T can be used along with mapping functions.
 *
 * @stateToIconMapping Optional function to map state to a specific icon.
 * @stateToClassMapping Optional function to map state to a specific CSS class for styling
 */
export class UiStateIndicatorComponent<T = StateIndicatorType> {
  public state = input.required<T>();
  public stateToIconMapping = input<((x: T) => IconsEnum) | undefined>();
  // New: optional mapping to CSS class names
  public stateToClassMapping = input<((x: T) => 'blue' | 'green' | 'red' | 'grey') | undefined>();

  public stateToIcon(stateType: T): IconsEnum {
    const mapping = this.stateToIconMapping();
    if (mapping) {
      return mapping(stateType);
    }
    return this.defaultStateToIcon(stateType as StateIndicatorType);
  }

  public stateClass(stateType: T): 'blue' | 'green' | 'red' | 'grey' {
    const mapping = this.stateToClassMapping();
    if (mapping) {
      return mapping(stateType);
    }
    return this.defaultStateToClass(stateType as StateIndicatorType);
  }

  private defaultStateToIcon(stateType: StateIndicatorType): IconsEnum {
    switch (stateType) {
      case StateIndicatorType.Connected:
        return IconsEnum.check_circle;
      case StateIndicatorType.Disconnected:
        return IconsEnum.close_circle;
      case StateIndicatorType.Pending:
        return IconsEnum.pending;
      case StateIndicatorType.Running:
        return IconsEnum.running;
      case StateIndicatorType.Paused:
        return IconsEnum.paused;
    }
  }

  private defaultStateToClass(stateType: StateIndicatorType): 'blue' | 'green' | 'red' | 'grey' {
    switch (stateType) {
      case StateIndicatorType.Connected:
        return 'green';
      case StateIndicatorType.Disconnected:
        return 'red';
      case StateIndicatorType.Running:
        return 'blue';
      case StateIndicatorType.Pending:
      case StateIndicatorType.Paused:
        return 'grey';
    }
  }
}
